import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCoachCollections,
  recordCoachHandover,
  getHandoverHistory,
  type CoachCollection,
  type HandoverRecord,
} from "../../services/api";

/**
 * Numerarul pe care îl țin antrenorii, și predarea lui.
 *
 * Was a page of its own next to "De încasat", which split one question in two:
 * a club admin chasing a fee and a club admin chasing the cash that fee was
 * paid with is the same person, in the same minute. So this is the panel, and
 * the receivables screen is where it lives.
 *
 * The thing worth seeing here is not a total but a location: money a parent has
 * already handed over, which the club has not received yet. Until somebody
 * records the handover it is in a coach's pocket, and that is the only state in
 * the system where cash exists outside the club without being a debt.
 */
export default function ClubCoachCash({ clubId }: { clubId: string | null | undefined }) {
  const { t } = useTranslation();

  const [coaches, setCoaches] = useState<CoachCollection[]>([]);
  const [allCoaches, setAllCoaches] = useState<CoachCollection[]>([]);
  const [handoverHistory, setHandoverHistory] = useState<HandoverRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"pending" | "all" | "history">("pending");

  // Handover modal
  const [handoverModal, setHandoverModal] = useState<{
    open: boolean;
    coach: CoachCollection;
    selectedPayments: string[];
    amount: number;
  } | null>(null);
  const [handoverNotes, setHandoverNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Statement modal (Situație)
  const [statementModal, setStatementModal] = useState<{
    open: boolean;
    coach: CoachCollection;
  } | null>(null);

  useEffect(() => {
    if (clubId) {
      loadData();
    }
  }, [clubId]);

  const loadData = async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const [collectionsData, historyData] = await Promise.all([
        getCoachCollections(clubId),
        getHandoverHistory(clubId),
      ]);
      setCoaches(collectionsData.coaches);
      setAllCoaches(collectionsData.all_coaches);
      setHandoverHistory(historyData);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleOpenHandover = (coach: CoachCollection) => {
    const allPaymentIds = coach.pending_payments.map(p => p.id);
    const totalAmount = coach.pending_payments.reduce((sum, p) => sum + p.amount_cents, 0);
    setHandoverModal({
      open: true,
      coach,
      selectedPayments: allPaymentIds,
      amount: totalAmount,
    });
    setHandoverNotes("");
  };

  const handleOpenStatement = (coach: CoachCollection) => {
    setStatementModal({
      open: true,
      coach,
    });
  };

  const handleTogglePayment = (paymentId: string, amount: number) => {
    if (!handoverModal) return;
    const isSelected = handoverModal.selectedPayments.includes(paymentId);
    if (isSelected) {
      setHandoverModal({
        ...handoverModal,
        selectedPayments: handoverModal.selectedPayments.filter(id => id !== paymentId),
        amount: handoverModal.amount - amount,
      });
    } else {
      setHandoverModal({
        ...handoverModal,
        selectedPayments: [...handoverModal.selectedPayments, paymentId],
        amount: handoverModal.amount + amount,
      });
    }
  };

  const handleRecordHandover = async () => {
    if (!handoverModal || handoverModal.selectedPayments.length === 0) return;
    setSaving(true);
    try {
      await recordCoachHandover({
        coach_id: handoverModal.coach.coach_id,
        amount_cents: handoverModal.amount,
        payment_ids: handoverModal.selectedPayments,
        notes: handoverNotes || undefined,
      });
      setHandoverModal(null);
      await loadData();
    } catch (err) {
      console.error("Failed to record handover:", err);
    }
    setSaving(false);
  };

  const formatCurrency = (cents: number, currency = "RON") => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate totals
  const totalPending = coaches.reduce((sum, c) => sum + c.pending_cash_cents, 0);

  return (
    <div className="space-y-6">
        <p className="text-sm text-slate-500">
          {t(
            "coachCash.intro",
            "Banii strânși cash de antrenori. Până înregistrezi predarea, ei sunt la antrenor — plata sportivului e deja făcută, dar clubul nu are încă banii."
          )}
        </p>

        {/* Summary Card */}
        {totalPending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-amber-800">{t("admin.totalPendingCash", "Total numerar de preluat")}</p>
                <p className="text-2xl font-bold text-amber-700">{formatCurrency(totalPending)}</p>
                <p className="text-xs text-amber-600">{coaches.length} {t("admin.coachesWithCash", "antrenori cu numerar")}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs — horizontally scrollable on mobile so each label has room
            to breathe instead of being squeezed to one or two characters. */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1 overflow-x-auto">
          <button
            onClick={() => setTab("pending")}
            className={`flex-shrink-0 sm:flex-1 py-2.5 px-3 sm:px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              tab === "pending" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("admin.pendingHandovers", "De preluat")} ({coaches.length})
          </button>
          <button
            onClick={() => setTab("all")}
            className={`flex-shrink-0 sm:flex-1 py-2.5 px-3 sm:px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              tab === "all" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("admin.allCoaches", "Toți antrenorii")} ({allCoaches.length})
          </button>
          <button
            onClick={() => setTab("history")}
            className={`flex-shrink-0 sm:flex-1 py-2.5 px-3 sm:px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              tab === "history" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("admin.handoverHistory", "Istoric")}
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : tab === "pending" ? (
          coaches.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
              <svg className="w-16 h-16 text-emerald-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-slate-500">{t("admin.noPendingCash", "Nu există numerar de preluat")}</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {coaches.map(coach => (
                <div key={coach.coach_id} className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-slate-100">
                  {/* On mobile: name + pending amount sit on the same row, but
                      they each wrap separately so neither truncates the other.
                      Email is intentionally dropped from the card to save space. */}
                  <div className="flex items-start gap-3 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-base sm:text-lg">
                      {coach.coach_name?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">{coach.coach_name}</h3>
                      <p className="text-xs sm:text-sm text-slate-500">
                        {coach.pending_payments_count} {t("admin.payments", "plăți")}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg sm:text-xl font-bold text-amber-600 whitespace-nowrap">{formatCurrency(coach.pending_cash_cents)}</p>
                    </div>
                  </div>

                  {/* Payments List — stack athlete & product vertically on
                      mobile so long names don't truncate to one character. */}
                  <div className="space-y-2 mb-3 sm:mb-4">
                    {coach.pending_payments.slice(0, 3).map(payment => (
                      <div key={payment.id} className="flex items-start justify-between gap-3 py-2 px-3 bg-slate-50 rounded-lg text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-700 truncate">{payment.athlete_name}</div>
                          <div className="text-xs text-slate-500 truncate">{payment.product_name}</div>
                        </div>
                        <span className="font-medium text-slate-700 whitespace-nowrap">{formatCurrency(payment.amount_cents, payment.currency)}</span>
                      </div>
                    ))}
                    {coach.pending_payments.length > 3 && (
                      <p className="text-sm text-slate-500 text-center py-2">
                        + {coach.pending_payments.length - 3} {t("admin.morePayments", "alte plăți")}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleOpenHandover(coach)}
                    className="w-full py-2.5 bg-indigo-500 text-white font-medium rounded-lg hover:bg-indigo-600 transition-colors"
                  >
                    {t("admin.recordHandover", "Înregistrează predare")}
                  </button>
                </div>
              ))}
            </div>
          )
        ) : tab === "all" ? (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 sm:px-5 py-3 text-left text-sm font-medium text-slate-600">{t("admin.coach", "Antrenor")}</th>
                  <th className="px-3 sm:px-5 py-3 text-right text-sm font-medium text-slate-600 whitespace-nowrap">{t("admin.pendingCash", "De preluat")}</th>
                  <th className="px-3 sm:px-5 py-3 text-right text-sm font-medium text-slate-600 whitespace-nowrap">{t("admin.totalHandedOver", "Total predat")}</th>
                  <th className="px-3 sm:px-5 py-3 text-center text-sm font-medium text-slate-600">{t("admin.actions", "Acțiuni")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allCoaches.map(coach => (
                  <tr key={coach.coach_id} className="hover:bg-slate-50">
                    <td className="px-3 sm:px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                          {coach.coach_name?.charAt(0) || "?"}
                        </div>
                        <p className="font-medium text-slate-800 truncate">{coach.coach_name}</p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-5 py-4 text-right whitespace-nowrap">
                      {coach.pending_cash_cents > 0 ? (
                        <span className="font-medium text-amber-600">{formatCurrency(coach.pending_cash_cents)}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-5 py-4 text-right whitespace-nowrap">
                      <span className="text-slate-700">{formatCurrency(coach.total_handed_over_cents)}</span>
                    </td>
                    <td className="px-3 sm:px-5 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenStatement(coach)}
                          className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap"
                        >
                          {t("admin.statement", "Situație")}
                        </button>
                        {coach.pending_cash_cents > 0 && (
                          <button
                            onClick={() => handleOpenHandover(coach)}
                            className="px-3 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors whitespace-nowrap"
                          >
                            {t("admin.withdraw", "Preia")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // History tab
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            {handoverHistory.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-slate-500">{t("admin.noHandoverHistory", "Nu există istoric de predări")}</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-sm font-medium text-slate-600">{t("admin.date", "Data")}</th>
                    <th className="px-5 py-3 text-left text-sm font-medium text-slate-600">{t("admin.coach", "Antrenor")}</th>
                    <th className="px-5 py-3 text-left text-sm font-medium text-slate-600">{t("admin.receivedBy", "Primit de")}</th>
                    <th className="px-5 py-3 text-right text-sm font-medium text-slate-600">{t("admin.amount", "Suma")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {handoverHistory.map(handover => (
                    <tr key={handover.id} className="hover:bg-slate-50">
                      <td className="px-5 py-4 text-sm text-slate-600">{formatDate(handover.handed_over_at)}</td>
                      <td className="px-5 py-4 font-medium text-slate-800">{handover.coach_name}</td>
                      <td className="px-5 py-4 text-slate-600">{handover.admin_name}</td>
                      <td className="px-5 py-4 text-right font-medium text-slate-800">
                        {formatCurrency(handover.amount_cents, handover.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Handover Modal */}
        {handoverModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">{t("admin.recordHandover", "Înregistrează predare")}</h2>
                  <p className="text-sm text-slate-500">{handoverModal.coach.coach_name}</p>
                </div>
                <button
                  onClick={() => setHandoverModal(null)}
                  className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 min-h-0 p-5 space-y-4 overflow-y-auto">
                {/* Select Payments */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    {t("admin.selectPayments", "Selectează plățile")}
                  </label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {handoverModal.coach.pending_payments.map(payment => (
                      <label
                        key={payment.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          handoverModal.selectedPayments.includes(payment.id)
                            ? "border-indigo-500 bg-indigo-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={handoverModal.selectedPayments.includes(payment.id)}
                            onChange={() => handleTogglePayment(payment.id, payment.amount_cents)}
                            className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                          />
                          <div>
                            <p className="text-sm font-medium text-slate-700">{payment.athlete_name}</p>
                            <p className="text-xs text-slate-500">{payment.product_name}</p>
                          </div>
                        </div>
                        <span className="font-medium text-slate-700">{formatCurrency(payment.amount_cents, payment.currency)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t("admin.totalSelected", "Total selectat")}</span>
                    <span className="text-xl font-bold text-slate-800">{formatCurrency(handoverModal.amount)}</span>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    {t("admin.notes", "Note")} ({t("common.optional", "opțional")})
                  </label>
                  <textarea
                    value={handoverNotes}
                    onChange={e => setHandoverNotes(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    rows={2}
                    placeholder={t("admin.handoverNotesPlaceholder", "Ex: Predat la data de...")}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
                <button
                  onClick={() => setHandoverModal(null)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleRecordHandover}
                  disabled={saving || handoverModal.selectedPayments.length === 0}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                >
                  {saving ? t("common.saving", "Se salvează...") : t("admin.confirmHandover", "Confirmă predarea")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statement Modal (Situație) */}
        {statementModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">{t("admin.coachStatement", "Situație încasări")}</h2>
                  <p className="text-sm text-slate-500">{statementModal.coach.coach_name}</p>
                </div>
                <button
                  onClick={() => setStatementModal(null)}
                  className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 min-h-0 p-5 space-y-4 overflow-y-auto">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <p className="text-sm text-amber-700">{t("admin.pendingCash", "De preluat")}</p>
                    <p className="text-xl font-bold text-amber-800">{formatCurrency(statementModal.coach.pending_cash_cents)}</p>
                    <p className="text-xs text-amber-600">{statementModal.coach.pending_payments_count} {t("admin.payments", "plăți")}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <p className="text-sm text-emerald-700">{t("admin.totalHandedOver", "Total predat")}</p>
                    <p className="text-xl font-bold text-emerald-800">{formatCurrency(statementModal.coach.total_handed_over_cents)}</p>
                  </div>
                </div>

                {/* Contact info — always render both rows so the admin sees
                    that a phone number isn't on file instead of guessing why
                    the field is missing. */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">{t("admin.contactInfo", "Date de contact")}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                    <div className="break-all">
                      <span className="text-slate-500">{t("admin.email", "Email")}:</span>{" "}
                      {statementModal.coach.coach_email ? (
                        <a href={`mailto:${statementModal.coach.coach_email}`} className="text-indigo-600 hover:underline">
                          {statementModal.coach.coach_email}
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                    <div>
                      <span className="text-slate-500">{t("admin.phone", "Telefon")}:</span>{" "}
                      {statementModal.coach.coach_phone ? (
                        <a href={`tel:${statementModal.coach.coach_phone}`} className="text-indigo-600 hover:underline">
                          {statementModal.coach.coach_phone}
                        </a>
                      ) : (
                        <span className="text-slate-400">{t("admin.noPhone", "nesetat")}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pending payments list */}
                {statementModal.coach.pending_payments.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3">{t("admin.pendingPaymentsDetail", "Plăți de preluat")}</h3>
                    <div className="space-y-2">
                      {statementModal.coach.pending_payments.map(payment => (
                        <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{payment.athlete_name}</p>
                            <p className="text-sm text-slate-500">{payment.product_name}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(payment.paid_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {t("admin.cash", "Numerar")}
                              </span>
                            </div>
                          </div>
                          <span className="font-bold text-amber-600">{formatCurrency(payment.amount_cents, payment.currency)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>{t("admin.noPendingPayments", "Nu are plăți de preluat")}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
                <button
                  onClick={() => setStatementModal(null)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
                >
                  {t("common.close", "Închide")}
                </button>
                {statementModal.coach.pending_cash_cents > 0 && (
                  <button
                    onClick={() => {
                      setStatementModal(null);
                      handleOpenHandover(statementModal.coach);
                    }}
                    className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm"
                  >
                    {t("admin.recordHandover", "Înregistrează predare")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
